<template>
  <div class="categories pt-0 pb-0 task-page" style="padding: 0 30px">
    <div class="d-flex justify-content-between mb-4">
      <div>
        <h6 class="f-w-600">Tasks</h6>
      </div>
      <div>
        <button class="btn btn-pill btn-secondary btn-sm" @click="openModalTask()">Create Task</button>
      </div>
    </div>

    <div v-if="tasks" class="mt-4 position-relative">
      <div v-if="!isDragging" class="isDragging"></div>

      <SortableList v-bind="{ useDragHandle: true, lockAxis: 'y'}" v-model="tasks" className="drag-list-group" @input="checkEnd">
        <SortableItem
          v-for="(item, index) in tasks"
          :key="index"
          :index="index"
          :item="item"
          :dayTimeSection_options="dayTimeSection_options"
          :daysOfWeekOptions="daysOfWeekOptions"
          @setCopyTask="copyTask"
          @setDeleteTask="deleteTask"
          @setOpenTask="openModalTask"
          @openTaskDetail="openTaskDetail"
        />
        <div class="add-another-task" v-if="tasks.length">
          <button class="btn btn-pill btn-secondary btn-sm" @click="openModalTask()">+ Add Another Task</button>
        </div>
      </SortableList>
    </div>

    <div v-else>
      <div role="alert" aria-live="polite" aria-atomic="true" class="alert alert alert-secondary outline alert-secondary">
        <p>There are no Tasks yet</p>
      </div>
    </div>
    <b-modal ref="details-task" :title="taskModalData ? taskModalData.title : ''" hide-footer>
      <div v-if="taskModalData">
        <span class="badge badge-success label-required" v-if="taskModalData.isRequired">Required</span>
        <p :class="taskModalData.isRequired ? 'pr-3' : ''">{{taskModalData.description}}</p>

        <hr />

        <div class="w-100 d-flex">
          <div class="task-content-info-inModal">
            <span>Days after the previous task: <b>{{taskModalData.delayDays}}</b></span>
            <span>Time Allotment: <b>{{taskModalData.timeAllotmentSection}}</b></span>
          </div>

          <div class="task-content-info-inModal">
            <span>Task Occurs: <b>{{taskModalData.numberOfOccurrences}}</b></span>
            <span>Days between each occurrence: <b>{{taskModalData.taskIntervalDays}}</b></span>
          </div>
        </div>

        <div class="user-profile mb-4 mt-4" v-if="taskModalData.link">
          <div class="ttl-info text-left link">
            <h6><i class="fa fa-link mr-2"></i>Link</h6>
            <span>
              <a :href="taskModalData.link" v-b-tooltip.hover :title="taskModalData.link" target="_blank">{{ taskModalData.link }}</a>
            </span>
          </div>
        </div>

        <hr />

        <div class="d-flex justify-content-between">
          <div class="user-profile">
            <div class="ttl-info text-left dayTimeSection" v-if="taskModalData.dayTimeSection">
              <h6><i class="fa fa-clock-o mr-2"></i>Day Time</h6>
              <span>{{ checkDayName(taskModalData.dayTimeSection) }}</span>
            </div>
            <div class="ttl-info text-left repeatingDaysOfWeek" v-if="taskModalData.repeatingDaysOfWeek">
              <h6><i class="fa fa-calendar mr-2"></i>Days of Week </h6>
              <span v-if="repeatingDaysOfWeekLength === 7">Every day</span>
              <span v-else v-for="(day, index) in taskModalData.repeatingDaysOfWeek" :key="index">{{ checkWeekDayName(day) }}</span>
            </div>
            <div class="ttl-info text-left location" v-if="taskModalData.location">
              <h6><i class="fa fa-location-arrow mr-2"></i>Location</h6>
              <span>{{ taskModalData.location }}</span>
            </div>
          </div>
        </div>

        <div class="w-100 mt-4 d-flex justify-content-end">
          <button class="btn btn-light btn-sm mr-2" @click="openModalTask(taskModalData);">
            Edit
          </button>
          <button class="btn btn-danger" @click="deleteTask(taskModalData)">
            Delete
          </button>
        </div>
      </div>
    </b-modal>
    <b-modal ref="add-task" title="Add Task" hide-footer>
      <form @submit.prevent="handleAdd">
        <div class="form-group">
          <label for="task-title">Title</label>
          <input type="text" v-model="task.title" id="task-title" name="title" class="form-control" :class="{ 'is-invalid': submitted && $v.task.title.$error }" />
          <div v-if="submitted && !$v.task.title.required" class="invalid-feedback">Title is required</div>
        </div>
        <div class="form-group">
          <label for="task-description">Description</label>
          <textarea rows="2" type="text" v-model="task.description" id="task-description" name="description" class="form-control" :class="{ 'is-invalid': submitted && $v.task.description.$error }"></textarea>
          <div v-if="submitted && !$v.task.description.required" class="invalid-feedback">Description is required</div>
        </div>
        <div class="form-group">
          <div class="col-form-label">Time of Day</div>
          <b-form-select class="form-control form-control-primary btn-square" v-model="task.dayTimeSection" :options="dayTimeSection_options"></b-form-select>
        </div>
        <div class="form-group">
          <label for="task-timeAllotmentSection">Time Allotment (minutes)</label>
          <input type="number" min="1" v-model="task.timeAllotmentSection" id="task-timeAllotmentSection" name="timeAllotmentSection" class="form-control" />
        </div>
        <div class="form-group">
          <label class="col-form-label">Previous Task</label>
          <b-form-select class="form-control form-control-primary btn-square" v-model="task.previousTaskId"
            :options="previousTasksOptions">
            <template #first>
              <b-form-select-option :value="null">-- Select a Task --</b-form-select-option>
            </template>
          </b-form-select>
        </div>
        <div v-if="task.previousTaskId" class="form-group">
          <label for="task-delayDays">How many days after the previous task should this task be scheduled?</label>
          <input type="number" min="0" v-model="task.delayDays" id="task-delayDays" name="delayDays" class="form-control"/>
        </div>
        <div class="form-group">
          <label for="task-numberOfOccurrences">How many times does this task occur?</label>
          <input type="number" min="1" v-model="task.numberOfOccurrences" id="task-numberOfOccurrences" name="numberOfOccurrences" class="form-control" />
        </div>
        <div class="form-group" v-if="task.numberOfOccurrences > 1">
          <label for="task-taskIntervalDays">How many off days in between each task occurrence?</label>
          <input type="number" min="0" v-model="task.taskIntervalDays" id="task-taskIntervalDays" name="taskIntervalDays" class="form-control" />
        </div>
        <div class="form-group">
          <div class="col-form-label">Days Of Week</div>
          <multiselect
            v-model="task.daysOfWeek" tag-placeholder="Add this as new tag" placeholder="You Can Choose More Than One"
            label="text" track-by="text" :options="daysOfWeekOptions" :multiple="true" :taggable="true"></multiselect>
        </div>
        <div class="form-group">
          <label for="task-location">Location (optional)</label>
          <input type="text" v-model="task.location" id="task-location" name="location" class="form-control" />
        </div>
        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <div class="checkbox">
                <b-form-checkbox name="isRequired" v-model="task.isRequired">Check if this Task is Required</b-form-checkbox>
              </div>
            </div>
          </div>
        </div>
        <div class="form-group">
          <button class="btn btn-primary">Create</button>
        </div>
      </form>
    </b-modal>
    <b-modal ref="edit-task" title="Edit task" hide-footer no-close-on-backdrop>
      <form @submit.prevent="handleEdit">
        <div class="form-group">
          <label for="task-edit-title">Title</label>
          <input type="text" v-model="taskEdit.title" id="task-edit-title" name="title" class="form-control" :class="{ 'is-invalid': submittedEdit && $v.taskEdit.title.$error }" />
          <div v-if="submittedEdit && !$v.taskEdit.title.required" class="invalid-feedback">Title is required</div>
        </div>
        <div class="form-group">
          <label for="task-edit-description">Description</label>
          <textarea rows="2" type="text" v-model="taskEdit.description" id="task-edit-description" name="description" class="form-control" :class="{ 'is-invalid': submittedEdit && $v.taskEdit.description.$error }"> </textarea>
          <div v-if="submittedEdit && !$v.taskEdit.description.required" class="invalid-feedback">Description is required</div>
        </div>
        <div class="form-group">
          <div class="col-form-label">Time of Day</div>
          <b-form-select class="form-control form-control-primary btn-square" v-model="taskEdit.dayTimeSection" :options="dayTimeSection_options"></b-form-select>
        </div>
        <div class="form-group">
          <label for="task-edit-timeAllotmentSection">Time Allotment (minutes)</label>
          <input type="number" min="1" v-model="taskEdit.timeAllotmentSection" id="task-edit-timeAllotmentSection" name="timeAllotmentSection" class="form-control" />
        </div>
        <div class="form-group">
          <label class="col-form-label">Previous Task</label>
          <b-form-select class="form-control form-control-primary btn-square" v-model="taskEdit.previousTaskId"
            :options="previousTasksOptions">
            <template #first>
              <b-form-select-option :value="null">-- Select a Task --</b-form-select-option>
            </template>
          </b-form-select>
        </div>
        <div v-if="taskEdit.previousTaskId" class="form-group">
          <label for="task-edit-delayDays">How many days after the previous task should this task be scheduled?</label>
          <input type="number" min="0" v-model="taskEdit.delayDays" id="task-edit-delayDays" name="delayDays" class="form-control"/>
        </div>
        <div class="form-group">
          <label for="task-edit-numberOfOccurrences">How many times does this task occur?</label>
          <input type="number" min="1" v-model="taskEdit.numberOfOccurrences" id="task-edit-numberOfOccurrences" name="numberOfOccurrences" class="form-control" />
        </div>
        <div class="form-group" v-if="taskEdit.numberOfOccurrences > 1">
          <label for="task-edit-taskIntervalDays">How many off days in between each task occurrence?</label>
          <input type="number" min="0" v-model="taskEdit.taskIntervalDays" id="task-edit-taskIntervalDays" name="taskIntervalDays" class="form-control" />
        </div>
        <div class="form-group">
          <div class="col-form-label">Days Of Week</div>
          <multiselect
            v-model="taskEdit.daysOfWeek" tag-placeholder="Add this as new tag" placeholder="Search or add a tag"
            label="text" track-by="text" :options="daysOfWeekOptions" :multiple="true" :taggable="true" name="name2"></multiselect>
        </div>
        <div class="form-group">
          <label for="task-edit-location">Location (optional)</label>
          <input type="text" v-model="taskEdit.location" id="task-edit-location" name="location" class="form-control" />
        </div>
        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <div class="checkbox">
                <b-form-checkbox name="isRequired" v-model="taskEdit.isRequired">Check if this Task is Required</b-form-checkbox>
              </div>
            </div>
          </div>
        </div>
        <div class="form-group">
          <button class="btn btn-primary" type="submit">Save Changes</button>
          <a class="btn btn-light ml-2" href="#" @click.prevent="$refs['edit-task'].hide()">Cancel </a>
        </div>
      </form>
    </b-modal>
  </div>
</template>

<script>
  import {required} from "vuelidate/lib/validators";
  import Multiselect from 'vue-multiselect';
  import SortableList from "./SortableList";
  import SortableItem from "./SortableItem";

  export default {
    name: "tasks",
    display: "Simple",
    order: 0,
    components: {
      SortableItem,
      SortableList,
      Multiselect
    },
    props: {
      milestoneId: {
        type: String,
        default: ""
      }
    },
    data() {
      return {
        draggingItem: [],
        tasks: [],
        openTask_data: [],
        maxSequence: 0,
        isDragging: true,
        previousTasksOptions: [],
        task: {
          title: "",
          description: "",
          dayTimeSection: "",
          delayDays: 0,
          numberOfOccurrences: 1,
          taskIntervalDays: 0,
          isRequired: true,
          completionContribution: 0,
          timeAllotmentSection: 1,
          isLinkAutomaticCompletion: true,
          location: "",
          acrsEvaluation: "",
          milestoneId: this.milestoneId,
          daysOfWeek: null,
          previousTaskId: null
        },
        taskEdit: {
          title: "",
          description: "",
          dayTimeSection: "",
          delayDays: 0,
          numberOfOccurrences: 1,
          taskIntervalDays: 0,
          isRequired: true,
          completionContribution: 0,
          timeAllotmentSection: 0,
          isLinkAutomaticCompletion: true,
          location: "",
          acrsEvaluation: "",
          milestoneId: this.milestoneId,
          daysOfWeek: [],
          previousTaskId: null
        },
        dayTimeSection_options:[
          { value: "", text:'Select One Value Only' },
          { value: "wakeup", text:'Morning' },
          { value: "beforeLunch", text:'Afternoon' },
          { value: "beforeSleep", text:'Evening' }
        ],
        daysOfWeekOptions: [
          { dayName: 'Sunday', text: 'Sun'},
          { dayName: 'Monday', text: 'Mon'},
          { dayName: 'Tuesday', text: 'Tues'},
          { dayName: 'Wednesday', text: 'Wed'},
          { dayName: 'Thursday', text: 'Thurs'},
          { dayName: 'Friday', text: 'Fri'},
          { dayName: 'Saturday', text: 'Sat'},
        ],
        submitted: false,
        submittedEdit: false,
        enabled: true,
        dragging: false,
        taskModalData: null
      }
    },
    validations: {
      task: {
        title: { required },
        description: { required },
      },
      taskEdit: {
        title: { required },
        description: { required },
      }
    },
    mounted() {
      this.getTasks();
    },
    methods: {
      getTasks() {
        this.tasks = [];
        this.maxSequence = null;
        
        if (this.milestoneId == '') {
          return;
        }
        
        const payload = {
          milestoneId: this.milestoneId
        };

        this.$store.dispatch('getTasks', payload)
        .then(async(val) => {
          if (val.data.statusCode !== 200) {
            return;
          }
          
          if (val.data.result && val.data.result.tasks) {
            let i = 0;
            for (const [key, value] of Object.entries(val.data.result.tasks)) {
              value.id = key;
              value.sequence = i;

              this.tasks.push(value);
              i++;
            }
            
            this.maxSequence = this.tasks.length - 1;
          }
        });
      },
      getPreviousTasks(isPrevious = true, taskId = '') {
        this.previousTasksOptions = [];
        
        const payload = {
          milestoneId: this.milestoneId
        };

        let url = 'getTasks';

        if (isPrevious) {
          url = 'getPreviousTasks';
        }

        this.$store.dispatch(url, payload)
          .then(async(resp) => {
            if (resp.data.statusCode !== 200) {
              return;
            }

            if (!resp.data.result) {
              return;
            }

            if (!resp.data.result.tasks) {
              return;
            }

            const rows = resp.data.result.tasks;

            for (const [key, value] of Object.entries(rows)) {
              if (key != taskId) {
                this.previousTasksOptions.push({
                  value: key,
                  text: value.title
                });
              }
            }
          });
      },
      openTaskDetail(item) {
        this.taskModalData = item
        this.$refs['details-task'].show()
      },
      openModalTask(task = null) {
        if (task) {
          if (task.sequence > 0) {
            this.getPreviousTasks(false, task.id);
          }
          else {
            this.getPreviousTasks(true, task.id);
          }

          this.openTask_data = task;
          this.changeDataVal('taskEdit', task);
          this.$refs['edit-task'].show();
          this.$refs['details-task'].hide();
        }
        else {
          if (this.tasks.length > 0) {
            this.getPreviousTasks(false);
          }
          else {
            this.getPreviousTasks(true);
          }

          this.task.previousTaskId = null;
          this.$refs['add-task'].show();
        }
      },
      handleAdd() {
        this.submitted = true;
        this.delayDays = +this.delayDays;
        this.task.sequence = this.maxSequence !== null ? (this.maxSequence + 1) : 0;

        this.$v.task.$touch();
        if (this.$v.task.$invalid) {
          return;
        }

        this.$store.dispatch('createTask', this.task).then(() => {
          this.$toasted.show(' Successfully added ', {
            theme: 'bubble',
            position: "top-right",
            type: 'success',
            duration: 2000
          });
          this.$refs['add-task'].hide();
          this.changeDataVal('task', null);
          this.submitted = false;
          this.getTasks();
        }).catch(() => {
          this.$toasted.show(' Please try again ', {
            theme: 'bubble',
            position: "top-right",
            type: 'error',
            duration: 2000
          });
        })
      },
      async handleEdit() {
        delete this.taskEdit.milestoneUID;
        delete this.taskEdit.goalUID;
        delete this.taskEdit.tracUID;

        this.submittedEdit = true;

        this.$v.taskEdit.$touch();
        if (this.$v.taskEdit.$invalid) {
          return;
        }

        this.taskEdit.numberOfOccurrences  = +this.taskEdit.numberOfOccurrences;
        this.taskEdit.taskIntervalDays  = +this.taskEdit.taskIntervalDays;
        delete this.taskEdit.repeatingDaysOfWeek;

        let res = await this.$store.dispatch('editTask', this.taskEdit);
        if (res.status === 200) {
          this.$toasted.show(' Successfully done ', {
            theme: 'bubble',
            position: "top-right",
            type: 'success',
            duration: 2000
          });
          this.$refs['edit-task'].hide();
          this.getTasks();
        }
      },
      changeDataVal(changedData, data, isForDrag, sequence) {
        this[changedData] = {};
        this[changedData] = {
          id: data ? data.id : "",
          milestoneId: data ? data.milestoneUID : this.milestoneId,
          title: data ? data.title : "",
          description: data ? data.description : "",
          dayTimeSection: data ? data.dayTimeSection : "",
          delayDays: data ? data.delayDays : 0,
          numberOfOccurrences: data ? data.numberOfOccurrences : 0,
          isRequired: data ? data.isRequired : true,
          completionContribution: data ? data.completionContribution : 0,
          timeAllotmentSection: data ? data.timeAllotmentSection : 1,
          isLinkAutomaticCompletion: data ? data.isLinkAutomaticCompletion : true,
          location: data ? data.location : "",
          acrsEvaluation: data ? data.acrsEvaluation : "",
          sequence: isForDrag ? sequence : ( data ? data.sequence : 0 ),
          daysOfWeek: [],
          taskIntervalDays: data ? (data.taskIntervalDays ? data.taskIntervalDays : 0) : 0,
          previousTaskId: data ? (data.previousTaskId ? data.previousTaskId : null) : null,
        };

        if (data && data.repeatingDaysOfWeek) {
          for (const value of Object.entries(data.repeatingDaysOfWeek)) {
            this[changedData].daysOfWeek.push({
              "dayName": value[1],
              "text": this.checkWeekDayName(value[1])
            })
          }
        }
      },
      checkWeekDayName(item) {
        let text = "";
        this.daysOfWeekOptions.forEach((val) => {
          if (
            val.dayName.toLowerCase() === item.toLowerCase() ||
            val.text.toLowerCase() === item.toLowerCase()
          ) {
            text = val.text
          }
        });

        return text;
      },
      deleteTask(task) {
        this.$swal({
          text:'Are you sure you want to do delete "' + task.title + '"?',
          showCancelButton: true,
          confirmButtonText: 'Yes',
          confirmButtonColor: '#4466f2',
          cancelButtonText: 'Cancel',
          cancelButtonColor: '#efefef',
          reverseButtons: true
        }).then((val) => {
          if (val.value) {
            this.$store.dispatch('deleteTask', task).then((val) => {
              if (val.data.statusCode && val.data.statusCode === 200) {
                this.$toasted.show(' Successfully done ', {
                  theme: 'bubble',
                  position: "top-right",
                  type: 'success',
                  duration: 2000
                });
                for (var i = this.tasks.length - 1; i >= 0; --i) {
                  if (this.tasks[i].id === task.id) {
                    this.tasks.splice(i,1);
                  }
                }

                this.$refs['details-task'].hide();
                this.taskModalData = null
              } else {
                this.$toasted.show(' Please try again ', {
                  theme: 'bubble',
                  position: "top-right",
                  type: 'error',
                  duration: 2000
                });
              }
            }).catch(() => {
              this.$toasted.show(' Please try again ', {
                theme: 'bubble',
                position: "top-right",
                type: 'error',
                duration: 2000
              });
            })
          }
        });

        this.maxSequence = this.tasks.length - 1;
      },
      checkDayName(item) {
        let text = "";
        this.dayTimeSection_options.forEach((val) => {
          if (
              val.value.toLowerCase() === item.toLowerCase() ||
              val.text.toLowerCase() === item.toLowerCase()
          ) {
            text = val.text
          }
        });

        return text;
      },
      checkMove(e) {
        this.draggingItem = [];
        let newOrderElement = e.draggedContext.element;
        let oldOrderElement = e.relatedContext.element;

        this.draggingItem = {
          one: {data: newOrderElement, sequence: oldOrderElement.sequence},
          two: {data: oldOrderElement, sequence: newOrderElement.sequence}
        };
      },
      async updateSequence(payload, sequence) {
        for (const value of Object.entries(this.tasks)) {
          if (value[1].id === payload.id) {
            value[1].sequence = sequence;
          }
        }

        await this.changeDataVal('taskEdit', payload, true, sequence);
        await this.$store.dispatch('editTask', this.taskEdit);
      },
      editTask() {
        this.$refs['edit-task'].show();
      },
      async checkEnd(newList) {
        this.dragging = false;
        this.isDragging = false;

        for await (const [index, value] of Object.entries(newList)) {
          this.updateSequence(value, index)
        }

        this.draggingItem = [];
        this.isDragging = true
      },
      trimMyString(string, maxLength, isSpace, start = 0) {
        if (string.length > maxLength) {
          let trimmedString = string.substr(start, maxLength);
          return (
              trimmedString.substr(
                  start,
                  Math.min(trimmedString.length, isSpace ? trimmedString.lastIndexOf(' ') : trimmedString.lastIndexOf(''))
              ) + ' ...'
          )
        }
        return string
      },
      copyTask(item) {
        this.$swal({
          text:'Are you sure you want to do copy "' + item.title + '?"',
          showCancelButton: true,
          confirmButtonText: 'Yes',
          confirmButtonColor: '#4466f2',
          cancelButtonText: 'Cancel',
          cancelButtonColor: '#efefef',
          reverseButtons: true
        }).then((val) => {
          if (val.value) {
            this.$store.dispatch('copyTask', item).then(() => {
              this.$toasted.show(' Successfully done ', {
                theme: 'bubble',
                position: "top-right",
                type: 'success',
                duration: 2000
              });
              this.getTasks();
            }).catch(() => {
              this.$toasted.show(' Please try again ', {
                theme: 'bubble',
                position: "top-right",
                type: 'error',
                duration: 2000
              });
            })
          }
        })
      },
    },
    computed: {
      repeatingDaysOfWeekLength() {
        if (!this.taskModalData.repeatingDaysOfWeek) return 0;
        return Object.keys(this.taskModalData.repeatingDaysOfWeek).length
      }
    }
  }
</script>

<style scoped lang="scss">
  @import url('https://unpkg.com/vue-multiselect@2.1.0/dist/vue-multiselect.min.css');
  .custom-select {
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
  }
  .invalid-feedback {
    display: block;
  }
  .custom-control {
    padding-left: 18px;
    z-index: 0;
  }
  .buttons {
    margin-top: 35px;
  }
  .ghost {
    overflow: hidden;
    position: relative;

    &:after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      z-index: 1111;
      background-color: #afe4ff;
    }
  }
  .task-content-info {
    display: flex;
    flex-direction: column;
  }
  .user-profile {
    display: flex;

    &.in-modal {
      flex-direction: column;

      .ttl-info {
        margin-bottom: 15px;
        &.repeatingDaysOfWeek {
          width: 100%;

          &>div {
            display: flex;
            flex-wrap: wrap;

            span {
              margin-bottom: 5px;
            }
          }
        }
      }
    }

    .ttl-info {
      margin-right: 20px;
      width: max-content;

      h6 {
        margin-bottom: 5px;
      }

      &.dayTimeSection {
        span {
          background: #1fa4ec;
          color: #fff;
          padding: 2px 7px;
          border-radius: 5px;
        }
        h6 {
          color: #1da6ec;
        }
      }

      &.repeatingDaysOfWeek {
         span {
           background: #4365f2;
           color: #fff;
           padding: 2px 7px;
           border-radius: 5px;
           margin-right: 5px;
         }
         h6 {
           color: #4365f2;
         }
       }

      &.link {
        span {
          margin-right: 5px;

          a {
            color: #1b252a;
          }
        }
        h6 {
          color: #22af48;
        }
      }
    }
  }

  .task-content-info-inModal {
    display: flex;
    flex-direction: column;
    width: 50%;

    &:first-child {
      padding-right: 10px;
    }

    &:last-child {
      padding-left: 10px;
    }
  }

  .isDragging {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1113;
  }

  .task-card {
    position: relative;
    overflow: hidden;

    .actions-wrapper {
      position: absolute;
      right: 0;
      top: 36px;
      flex-direction: column;

      button {
        width: 36px;
        padding: 10px 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 0;

        i {
          margin: 0;
        }
      }
    }

    .move-btn {
      position: absolute;
      top: 0;
      right: 0;
      overflow: hidden;
      opacity: 0;
      transition: 0.3s;
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: move;
      background-color: #8ba2c4;

      img {
        width: 18px;
        height: 18px;
        margin: 0;
      }
    }

    &:hover .move-btn {
      overflow: visible;
      opacity: 1;
    }
  }
  .label-required {
    position: absolute;
    right: -20px;
    transform: rotate(90deg);
    border-radius: 0;
  }
  .add-another-task {
    display: flex;
    justify-content: center;
  }
</style>
